import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { CookieManager } from "../../utils/simpleCookieManager";
import {
  CookieManager as SharedCookieManager,
  SessionLifecycleClient,
} from "@repo/auth-sdk";
import { motion } from "framer-motion";
import { Button, TextInput, useMutationWithToast } from "@repo/ui";
import { EnvelopeIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { AuthenticationService } from "../../services/index";
import TIGLogoColoured from "@repo/ui/src/assets/images/TIG_LOGO_COLORED.svg";

type LoginFormType = { email: string; password: string };

export function Login() {
  const loginForm = useForm<LoginFormType>();
  const loginMutation = useMutationWithToast(AuthenticationService.login);

  const onSubmit = loginForm.handleSubmit((formData) => {
    if (!formData.email || formData.password.length < 3) return;
    loginMutation.mutate(formData, {
      async onSuccess(data) {
        const tokens = {
          accessToken: `${data.data.accessToken}`,
          tokenType: "Bearer",
        };
        // A successful login always starts a fresh server session. This avoids
        // reusing a stale opaque ID left behind after a browser/token expiry.
        SharedCookieManager.clearSessionId();
        CookieManager.setAuthTokens(tokens, data.data.user);
        await SessionLifecycleClient.ensureSession({
          clientClass: "auth_web",
        });
        window.location.replace(`${data.data.user.redirectUrl}`);
      },
    });
  });

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-sm"
      >
        {/* Logo — visible only on mobile where the left panel is hidden */}
        <img src={TIGLogoColoured} alt="TIG" className="w-28 mb-6 lg:hidden" />

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10">
          <div className="mb-8">
            <h2
              className="font-bold text-gray-900 mb-1"
              style={{ fontSize: "1.5rem" }}
            >
              Welcome back
            </h2>
            <p className="text-gray-400 text-md">
              Sign in to continue to your portal
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <TextInput
              label="Email address"
              placeholder="you@example.com"
              fullWidth
              control={loginForm.control}
              name="email"
              type="email"
              startAdornment={<EnvelopeIcon className="h-5 w-5" />}
            />

            <div>
              <TextInput
                control={loginForm.control}
                name="password"
                fullWidth
                label="Password"
                placeholder="Enter your password"
                type="password"
                startAdornment={<LockClosedIcon className="h-5 w-5" />}
              />
              <div className="flex justify-end mt-1.5">
                <Link
                  to="/forgot-password"
                  className="inline-flex min-h-10 items-center text-base font-medium text-primary-main transition-colors hover:text-primary-dark hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              loading={loginMutation.isPending}
              type="submit"
              className="w-full mt-1"
            >
              Sign In
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
